import { Button, Timeline, message } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import TroubleshootingStepRowDisplay from './TroubleshootingStepRowDisplay';
import {
	troubleshootingGuidesRestCrudThunksForBuyer,
	troubleshootingGuidesRestCrudThunksForSupplier,
} from '../../../thunks/troubleshooting_guides_thunks';
import TSStepDisplayInTimeline from './TSStepDisplayInTimeline';
import { PlusOutlined } from '@ant-design/icons';
import isEqual from 'lodash/isEqual';
import AccessPermissionChecker from '../../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../../utils/AuthUtils';
import { withUserType } from '../../withUserType';
import { ROLE_TYPES } from '../../../utils/DataConstants';

const removeNewSteps = (steps) =>
	steps
		.filter((_) => !_.isNew)
		.map((step) => ({
			...step,
			multipleChoices: nullSafeGetOrElse('multipleChoices', step, []).map((mc) => ({
				...mc,
				subSteps: removeNewSteps(nullSafeGetOrElse('subSteps', mc, [])),
			})),
		}));

const getInitialSteps = (guide) =>
	nullSafeGetOrElse(
		'draftTroubleshootingSteps',
		guide,
		nullSafeGetOrElse('troubleshootingSteps', guide, [])
	)
		.sort((a, b) => parseInt(a.orderingId || 0) - parseInt(b.orderingId || 0))
		.map((_, idx) => ({ ..._, orderingId: idx + 1 }));

const TroubleshootingForm: FC<any> = ({
	formData,
	isLoading,
	updatePartially,
}): React.ReactElement => {
	const [steps, setSteps] = useState(getInitialSteps(formData));
	const [savedSteps, setSavedSteps] = useState(getInitialSteps(formData));

	const onAddStep = () => {
		setSteps((st) => {
			const existingNewStep = st.find((_) => _.isNew);
			return existingNewStep ? st : [...st, ...[{ isNew: true }]];
		});
	};

	const onCancelNewStep = () => setSteps((st) => st.filter((_) => !_.isNew));

	const saveDraft = useCallback(
		(newSteps) => {
			return new Promise((resolve, reject) => {
				updatePartially(formData.id, {
					...formData,
					draftTroubleshootingSteps: newSteps,
				})
					.then((_) => {
						setSteps(newSteps);
						message.success('Draft saved successfully!');
						resolve(newSteps);
					})
					.catch((err) => {
						message.error(err);
						reject(err);
					});
			});
		},
		[formData, updatePartially]
	);

	const onStepEditSuccess = useCallback(
		(step) => {
			const newSteps = !!step.orderingId
				? steps.map((_) => (_.orderingId === step.orderingId ? step : _))
				: steps.map((existingStep) =>
						existingStep.isNew
							? {
									...step,
									isNew: false,
									orderingId: steps.length + 1,
							  }
							: existingStep
				  );
			return new Promise((resolve, reject) => {
				const sanitisedSteps = removeNewSteps(newSteps);
				const withSave = !isEqual(savedSteps, sanitisedSteps);
				if (withSave) {
					saveDraft(sanitisedSteps)
						.then((_) => {
							resolve(_);
							setSavedSteps(sanitisedSteps);
							setSteps(newSteps);
						})
						.catch(reject);
				} else {
					setSteps(newSteps);
					resolve(newSteps);
				}
			});
		},
		[saveDraft, savedSteps, steps]
	);

	const onDeleteStep = useCallback(
		(step) => {
			const newSteps = steps
				.sort((a, b) => parseInt(a.orderingId || 0) - parseInt(b.orderingId || 0))
				.filter((_) => _.orderingId !== step.orderingId)
				.map((_, idx) => ({ ..._, orderingId: idx + 1 }));

			saveDraft(newSteps);
		},
		[saveDraft, steps]
	);

	return (
		<div>
			<Timeline>
				{steps.map((step, idx) => {
					const stepNo = idx + 1;
					return (
						<Timeline.Item position="right">
							<TroubleshootingStepRowDisplay
								step={step}
								stepNo={stepNo}
								editMode
								onEditStep={onStepEditSuccess}
								onCancelNewStep={onCancelNewStep}
								onDeleteStep={onDeleteStep}
								isLoading={isLoading}
								saveDraft={saveDraft}
							/>
						</Timeline.Item>
					);
				})}
				<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING}>
					<Timeline.Item>
						<TSStepDisplayInTimeline>
							<Button
								type="primary"
								className="-mt-[12px]"
								ghost
								icon={<PlusOutlined />}
								onClick={onAddStep}
							>{`Add Step ${steps.length + 1}`}</Button>
						</TSStepDisplayInTimeline>
					</Timeline.Item>
				</AccessPermissionChecker>
			</Timeline>
		</div>
	);
};

const mapStateToProps = (state) => ({
	isLoading: state.troubleshooting_guides.creating || state.troubleshooting_guides.updating,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updatePartially: (id, entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? troubleshootingGuidesRestCrudThunksForSupplier.updatePartially(id, entity)
				: troubleshootingGuidesRestCrudThunksForBuyer.updatePartially(id, entity)
		),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(TroubleshootingForm as any)
	) as any
);
